/**
 * `/plans/nowy` with no plan in this tab — a reload, a crash, a second tab, or the address typed
 * bare. When this device kept the unsaved plan the notice asks whether to restore it. When it did
 * not, the address starts what it names: the create window the library's „+ Nowy program" opens,
 * so the plan is still described by the trainer and never seeded blank. It said „Ten szkic już nie
 * istnieje" until 2 November 2026, which a bare visit read as an expired draft nobody had made;
 * the address carries no draft id, so a stale draft and a bare visit cannot be told apart, and the
 * way forward is the same for both. Dismissed, the notice keeps the button to reopen it. While the
 * signed-in user is still unconfirmed it waits, like any other read.
 *
 * The notice chrome is this page's `BuilderNotice`, the same box every non-week state stands in; the
 * decision and the plan's card and answers are `features/create-plan`'s. „Usuń program" removes
 * its own button, so focus is put on the heading first — the one element that stays, and which is
 * about to say there is nothing left to restore.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CreatePlanDialog, LeftoverPlanAnswers, useUnsavedPlanRecovery } from '~/features/create-plan';

import { BuilderNotice } from './BuilderNotice';

export function NewPlanNotice({ escape }: { readonly escape: ReactNode }) {
  const { t } = useTranslation();
  const recovery = useUnsavedPlanRecovery();
  const heading = useRef<HTMLHeadingElement>(null);
  /* Opened by ARRIVING with nothing to restore, decided once the read settles. Not by reaching
   * „nothing" later: „Usuń program" lands there too, and a create window springing open over the
   * trainer's own deletion is not what they asked for. */
  const [creating, setCreating] = useState<boolean | null>(null);
  if (creating === null && recovery.status !== 'pending') {
    setCreating(recovery.status === 'nothing');
  }

  if (recovery.status === 'pending') {
    return <BuilderNotice title={t('web:planBuilder.loading')} busy />;
  }
  if (recovery.status === 'nothing') {
    return (
      <>
        <BuilderNotice
          titleRef={heading}
          title={t('web:planCreate.title')}
          action={{
            label: t('web:planCreate.open'),
            onClick: () => {
              setCreating(true);
            },
          }}
          escape={escape}
        />
        <CreatePlanDialog
          open={creating === true}
          finalFocus={() => heading.current}
          onClose={() => {
            setCreating(false);
          }}
        />
      </>
    );
  }
  return (
    <BuilderNotice
      titleRef={heading}
      title={t('web:planResume.restoreTitle')}
      message={t('web:planResume.restoreBody')}
    >
      <LeftoverPlanAnswers
        plan={recovery.plan}
        onRestore={recovery.restore}
        onRemove={() => {
          heading.current?.focus();
          recovery.remove();
        }}
      />
    </BuilderNotice>
  );
}
